import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';
import { useLanguage } from '../context/languagecontext';

export const Testimonials = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const next = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  const current = testimonials[currentIndex];

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-wide">
            {t('testimonials.title')}
          </h2>
          <p className="font-body text-xl text-gray-400 max-w-3xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="bg-slate-800 border-2 border-orange-500 rounded-sm p-12 relative shadow-2xl"
              >
                <Quote className="absolute top-8 left-8 w-16 h-16 text-orange-500/20" />
                <div className="flex items-center justify-center space-x-1 mb-8">
                  {[...Array(current.rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <p className="font-body text-gray-200 text-xl leading-relaxed mb-8 text-center">
                  "{current.text}"
                </p>
                <div className="text-center border-t-2 border-slate-700 pt-6">
                  <div className="font-heading text-white text-2xl font-bold uppercase tracking-wider">
                    {current.name}
                  </div>
                  <div className="font-body text-gray-400 mt-2">{current.location}</div>
                  <div className="inline-block bg-orange-500/10 border border-orange-500/30 rounded-sm px-4 py-2 mt-3">
                    <span className="font-body text-orange-500 text-sm font-bold uppercase tracking-wider">
                      {current.project}
                    </span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <button
              onClick={prev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-sm hidden lg:flex items-center justify-center"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={next}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-sm hidden lg:flex items-center justify-center"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          <div className="flex justify-center items-center gap-4 mt-8 lg:hidden">
            <button onClick={prev} className="w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-sm flex items-center justify-center">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={index === currentIndex ? 'w-8 h-3 rounded-sm bg-orange-500' : 'w-3 h-3 rounded-sm bg-slate-700'}
                />
              ))}
            </div>
            <button onClick={next} className="w-12 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-sm flex items-center justify-center">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          <div className="hidden lg:flex justify-center items-center gap-3 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={index === currentIndex ? 'w-12 h-2 rounded-sm bg-orange-500' : 'w-2 h-2 rounded-sm bg-slate-700'}
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-20">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              onClick={() => setCurrentIndex(index)}
              className="bg-slate-800/50 rounded-sm p-6 border border-slate-700 hover:border-orange-500/50 transition-all cursor-pointer group"
            >
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-orange-500 text-orange-500" />
                ))}
              </div>
              <p className="font-body text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3">
                "{testimonial.text}"
              </p>
              <div className="border-t border-slate-700 pt-3">
                <div className="font-heading text-white text-sm font-bold uppercase tracking-wider">
                  {testimonial.name}
                </div>
                <div className="font-body text-xs text-gray-400 mt-1">{testimonial.location}</div>
              </div>
              <div className="w-0 h-1 bg-orange-500 group-hover:w-full transition-all duration-300 mt-3"></div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};
